import type { AxiosInstance } from "axios";
import { useClient } from "../state/client";
import { initialized } from "../utils/initializedUtil";
import type CustomerId from "../state/CustomerId";
import { useQuery } from "@tanstack/solid-query";
import { hours } from "../utils/timeUtil";
import type { Customer } from "../state/customer";
import { toCustomer } from "../utils/mappers/customer";
import { doNotRetryOnUnrecoverableError } from "../utils/apiUtil";

export type CustomerDto = {
  id: string;
  customerId: number;
  name: string;
  unlockedFeatures: string[];
  logoAssetId: string | null;
  settings: {
    dateFormat: string;
    segment: string;
    otherSegment?: string;
    size: string;
  };
  meta: {
    deleteOn?: string; // yyyy-MM-dd
    personalisation?: {
      referralSource?: string;
      referralSourceOther?: string;
      goals?: string[];
      goalsOther?: string;
    };
  };
};

export type CustomerInfoDto = {
  customerId: number;
  name: string;
};

let client: AxiosInstance | null = null;
const init = (): void => {
  client = useClient();
};
const getClient = () => initialized(client, "client");

const getCustomer = (customerId: CustomerId) =>
  useQuery<Customer>(() => ({
    queryKey: ["customer"],
    queryFn: async () => {
      const { data } = await getClient().get<CustomerDto>(`api/v1.0/customers/${customerId}`);
      return toCustomer(data);
    },
    staleTime: hours(24),
    retry: doNotRetryOnUnrecoverableError,
  }));

const getCustomers = async () => {
  const { data } = await getClient().get<CustomerInfoDto[]>(`api/v1.0/customers`, {
    params: { filter: "ALL" },
  });
  return data;
};

const CustomerService = {
  init,
  getCustomer,
  getCustomers,
};

export default CustomerService;
